import { useState } from 'react';
import { useRoute, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Star, Heart, ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { resolveEatzyMediaUrl } from '@/lib/utils';
import { AuthenticatedAvatarImage } from '@/components/AuthenticatedAvatarImage';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TrustBadge, { getTrustTier } from '../components/TrustBadge';
import FollowButton from '../components/FollowButton';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface PublicProfile {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  bio: string;
  trustScore: number;
  trustTier: string;
  kudozBalance: number;
  stats: {
    reviews: number;
    followers: number;
    following: number;
    averageRating: number;
  };
  favoriteRestaurants: Array<{
    id: string;
    name: string;
    rating: number;
  }>;
  reviews: Array<{
    id: number;
    restaurantName: string;
    restaurantId: string;
    rating: number;
    text: string;
    mediaUrls: string[] | null;
    tags: string[] | null;
    likesCount: number;
    createdAt: string;
  }>;
}

const PageHeader = ({ onBack }: { onBack: () => void }) => (
  <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
    <Button variant="ghost" size="icon" onClick={onBack} data-testid="button-back">
      <ArrowLeft className="w-5 h-5" />
    </Button>
    <h1 className="text-lg font-semibold">Profile</h1>
    <div className="w-10" />
  </div>
);

export default function FriendProfile() {
  const [, params] = useRoute('/profile/:userId');
  const [, navigate] = useLocation();
  const userId = params?.userId;
  const [trustVote, setTrustVote] = useState<boolean | null>(null);

  const { data: profile, isLoading, error } = useQuery<PublicProfile>({
    queryKey: [`/api/users/${userId}/public-profile`],
    enabled: !!userId,
  });

  const goBack = () => navigate('/?tab=friends');

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-background">
        <PageHeader onBack={goBack} />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex flex-col h-full bg-background">
        <PageHeader onBack={goBack} />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">User not found</p>
        </div>
      </div>
    );
  }

  const getDisplayImageUrl = (url: string) => url;

  return (
    <div className="flex flex-col h-full bg-background">
      <PageHeader onBack={goBack} />

      <div className="flex-1 overflow-y-auto lg:overflow-hidden">
        <div className="lg:grid lg:grid-cols-[380px_1fr] lg:items-start lg:h-full">

          <div className="lg:border-r lg:border-border lg:sticky lg:top-0 lg:overflow-y-auto lg:h-full p-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="w-20 h-20 flex-shrink-0">
                    <AuthenticatedAvatarImage src={resolveEatzyMediaUrl(profile.avatar)} />
                    <AvatarFallback className="text-2xl">
                      {(profile.displayName || profile.username || '?').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold truncate" data-testid="text-user-name">
                      {profile.displayName || profile.username}
                    </h2>
                    <p className="text-sm text-muted-foreground">@{profile.username}</p>
                    {profile.bio && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{profile.bio}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <TrustBadge
                        trustScore={profile.trustScore}
                        trustTier={getTrustTier(profile.trustScore)}
                        size="md"
                        showScore={false}
                        showLabel={true}
                        showBar={false}
                        data-testid="trust-badge-profile"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t">
                  <div className="text-center" data-testid="stat-reviews">
                    <div className="font-bold text-lg">{profile.stats.reviews}</div>
                    <div className="text-xs text-muted-foreground">Reviews</div>
                  </div>
                  <div className="text-center" data-testid="stat-followers">
                    <div className="font-bold text-lg">{profile.stats.followers}</div>
                    <div className="text-xs text-muted-foreground">Followers</div>
                  </div>
                  <div className="text-center" data-testid="stat-following">
                    <div className="font-bold text-lg">{profile.stats.following}</div>
                    <div className="text-xs text-muted-foreground">Following</div>
                  </div>
                  <div className="text-center" data-testid="stat-avg-rating">
                    <div className="font-bold text-lg flex items-center justify-center gap-1">
                      {profile.stats.averageRating.toFixed(1)}
                      <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                    </div>
                    <div className="text-xs text-muted-foreground">Avg</div>
                  </div>
                </div>

                <div className="mt-6">
                  <FollowButton
                    targetUserId={profile.id}
                    size="default"
                    variant="default"
                    showText={true}
                    className="w-full"
                  />
                </div>

                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">Trust this reviewer?</span>
                      <TrustBadge
                        trustScore={profile.trustScore}
                        trustTier={getTrustTier(profile.trustScore)}
                        size="xs"
                        showScore={false}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 w-full">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTrustVote(trustVote === true ? null : true)}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-1.5",
                        trustVote === true && "bg-kudoz/10 text-kudoz border-kudoz no-default-hover-elevate"
                      )}
                      data-testid="button-trust-reviewer"
                    >
                      <ThumbsUp className="w-4 h-4" />
                      <span className="text-sm font-medium">Trust</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTrustVote(trustVote === false ? null : false)}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-1.5",
                        trustVote === false && "bg-destructive/10 text-destructive border-destructive no-default-hover-elevate"
                      )}
                      data-testid="button-distrust-reviewer"
                    >
                      <ThumbsDown className="w-4 h-4" />
                      <span className="text-sm font-medium">Distrust</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:overflow-y-auto lg:h-full p-4 pb-20">
            <Tabs defaultValue="reviews">
              <TabsList className="w-full grid grid-cols-2">
                <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
                <TabsTrigger value="favorites" data-testid="tab-favorites">Favorites</TabsTrigger>
              </TabsList>

              <TabsContent value="reviews" className="space-y-3 mt-4">
                {profile.reviews.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No reviews yet</p>
                  </div>
                ) : (
                  profile.reviews.map((review) => (
                    <Card key={review.id} className="hover-elevate" data-testid={`review-${review.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="min-w-0">
                            <h3 className="font-semibold truncate">{review.restaurantName || 'Restaurant'}</h3>
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="w-4 h-4 fill-yellow-500 text-yellow-500 flex-shrink-0" />
                              <span className="font-semibold">{(review.rating || 0).toFixed(1)}</span>
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {review.createdAt
                              ? formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })
                              : ''}
                          </span>
                        </div>
                        {review.text && (
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{review.text}</p>
                        )}
                        {review.mediaUrls && review.mediaUrls.length > 0 && (
                          <div className="aspect-video w-full rounded-md overflow-hidden mb-3">
                            <img
                              src={getDisplayImageUrl(review.mediaUrls[0])}
                              alt="Review"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        {review.tags && review.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {review.tags.slice(0, 4).map((tag, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Heart className="w-4 h-4" />
                          <span>{review.likesCount} likes</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>

              <TabsContent value="favorites" className="space-y-3 mt-4">
                {profile.favoriteRestaurants.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No favorite restaurants yet</p>
                  </div>
                ) : (
                  profile.favoriteRestaurants.map((restaurant, index) => (
                    <Card
                      key={restaurant.id || index}
                      className="hover-elevate active-elevate-2 cursor-pointer"
                      onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                      data-testid={`favorite-${restaurant.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-semibold truncate">{restaurant.name}</h3>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                            <span className="font-semibold">{(restaurant.rating || 0).toFixed(1)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

        </div>
      </div>
    </div>
  );
}
